import React from "react";

// Photo-realistic peptide vial that matches the Peptigen Labs reference photo:
// blue cap, dark navy label with rising-graph logo + Peptigen Labs wordmark,
// large white peptide name, lyophilised white powder at vial base, lab background.

// If product.image_url is set, render the real image instead of SVG.

const capColours = {
  // category -> cap colour (used as the rubber/plastic cap on the SVG fallback)
  "Metabolic Research": "#1F8FA8",
  "Growth Hormone Secretagogue Research": "#2EAFB7",
  "Repair & Recovery Research": "#5C9E1F",
  "Longevity & Cellular Research": "#1F8FA8",
  "Cognitive & Neurological Research": "#5C6678",
  "Other Research Peptides": "#A33470",
  "Laboratory Reagents": "#1F8FA8",
};

export default function ProductVial({ product, size = "md", outOfStock = false }) {
  const dim = size === "lg" ? 360 : size === "sm" ? 130 : 240;

  // Out-of-stock placeholder takes priority — replaces both the photo and the SVG vial
  if (outOfStock) {
    return <OutOfStockPlaceholder productName={product?.name} />;
  }

  // Real image takes priority if provided
  if (product?.image_url) {
    return (
      <div
        className="relative w-full h-full overflow-hidden flex items-center justify-center"
        style={{ background: "#0B1220" }}
        aria-label={`${product?.name} — Peptigen Labs research vial`}
      >
        <img
          src={product.image_url}
          alt={`${product.name} ${product.strength} — Peptigen Labs`}
          loading="lazy"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center" }}
        />
      </div>
    );
  }

  const capColor = capColours[product?.category] || "#1F8FA8";
  const id = `v-${(product?.sku || "x").replace(/[^a-zA-Z0-9]/g, "")}`;
  const name = (product?.name || "").toUpperCase();
  // Auto-fit name font size based on length
  const nameFontSize = name.length > 12 ? 11 : name.length > 9 ? 13 : 16;

  return (
    <div
      className="relative w-full h-full overflow-hidden flex items-center justify-center"
      aria-label={`${product?.name || "Peptigen Labs"} research vial`}
      style={{
        background:
          "radial-gradient(ellipse at 30% 30%, rgba(40,90,140,0.25) 0%, transparent 50%), radial-gradient(ellipse at 75% 65%, rgba(20,60,100,0.30) 0%, transparent 55%), linear-gradient(180deg, #06101F 0%, #020812 100%)",
      }}
    >
      {/* Lab background — DNA helix silhouette + shelving blur */}
      <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 400 400" aria-hidden="true">
        <defs>
          <filter id={`${id}-blur`}><feGaussianBlur stdDeviation="3" /></filter>
        </defs>
        {/* helix on right */}
        <g filter={`url(#${id}-blur)`} opacity="0.55">
          <path d="M340 30 Q360 80 340 130 Q320 180 340 230 Q360 280 340 330 Q320 380 340 400" stroke="#1A4A7A" strokeWidth="6" fill="none" />
          <path d="M380 30 Q360 80 380 130 Q400 180 380 230 Q360 280 380 330 Q400 380 380 400" stroke="#1A4A7A" strokeWidth="6" fill="none" />
          {[60, 110, 160, 210, 260, 310, 360].map((y) => (
            <line key={y} x1="340" y1={y} x2="380" y2={y} stroke="#1A4A7A" strokeWidth="2" />
          ))}
        </g>
        {/* shelving blur on left */}
        <g filter={`url(#${id}-blur)`} opacity="0.45">
          <rect x="0" y="80" width="80" height="6" fill="#1A2F4A" />
          <rect x="0" y="160" width="100" height="6" fill="#1A2F4A" />
          <rect x="0" y="240" width="60" height="6" fill="#1A2F4A" />
          {/* a few mini bottles */}
          <rect x="20" y="50" width="14" height="32" fill="#0B2940" />
          <rect x="50" y="50" width="14" height="32" fill="#0B2940" />
          <rect x="20" y="130" width="14" height="32" fill="#0B2940" />
        </g>
        {/* highlight from above */}
        <ellipse cx="200" cy="0" rx="200" ry="80" fill="#3a6b9a" opacity="0.18" filter={`url(#${id}-blur)`} />
      </svg>

      <svg viewBox="0 0 240 360" width={dim} height={dim * 1.5} className="relative z-10 max-w-[78%] max-h-[95%]">
        <defs>
          {/* Glass body */}
          <linearGradient id={`${id}-glass`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0E1626" stopOpacity="0.95" />
            <stop offset="20%" stopColor="#1A2940" stopOpacity="0.55" />
            <stop offset="50%" stopColor="#101A2A" stopOpacity="0.20" />
            <stop offset="80%" stopColor="#1A2940" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#06101F" stopOpacity="0.95" />
          </linearGradient>
          {/* Aluminium crimp */}
          <linearGradient id={`${id}-alu`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5C6678" />
            <stop offset="20%" stopColor="#C8CCD6" />
            <stop offset="50%" stopColor="#F0F2F6" />
            <stop offset="80%" stopColor="#C8CCD6" />
            <stop offset="100%" stopColor="#5C6678" />
          </linearGradient>
          {/* Cap (blue rubber/plastic) */}
          <radialGradient id={`${id}-cap`} cx="50%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#3FB3D6" />
            <stop offset="60%" stopColor={capColor} />
            <stop offset="100%" stopColor="#0E4A6A" />
          </radialGradient>
          {/* Powder pile */}
          <linearGradient id={`${id}-powder`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D0D8E5" stopOpacity="0.85" />
          </linearGradient>
          {/* Label */}
          <linearGradient id={`${id}-label`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#070D18" />
            <stop offset="50%" stopColor="#0E1828" />
            <stop offset="100%" stopColor="#070D18" />
          </linearGradient>
          {/* Accent line (cyan to lime) */}
          <linearGradient id={`${id}-accent`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5EEAD4" />
            <stop offset="100%" stopColor="#A6FF00" />
          </linearGradient>
          {/* Floor shadow */}
          <radialGradient id={`${id}-shadow`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#000" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* table reflection */}
        <ellipse cx="120" cy="338" rx="70" ry="6" fill={`url(#${id}-shadow)`} />

        {/* === BLUE CAP (rounded dome) === */}
        <ellipse cx="120" cy="44" rx="42" ry="10" fill={`url(#${id}-cap)`} />
        <rect x="78" y="40" width="84" height="14" fill={`url(#${id}-cap)`} />
        <ellipse cx="120" cy="54" rx="42" ry="6" fill="#0E4A6A" opacity="0.6" />
        {/* cap top highlight */}
        <ellipse cx="108" cy="38" rx="20" ry="3" fill="#FFFFFF" opacity="0.30" />

        {/* === ALUMINIUM CRIMP === */}
        <rect x="78" y="56" width="84" height="20" fill={`url(#${id}-alu)`} />
        {[60, 64, 68, 72].map((y) => (
          <line key={y} x1="78" y1={y} x2="162" y2={y} stroke="#5C6678" strokeWidth="0.4" opacity="0.5" />
        ))}
        {/* crimp rolled lip bottom */}
        <path d="M75 76 Q77 80 82 80 L158 80 Q163 80 165 76 Z" fill="#9098A8" />

        {/* === RUBBER STOPPER (visible just below crimp) === */}
        <rect x="86" y="76" width="68" height="4" fill="#1A1410" opacity="0.85" />

        {/* === GLASS NECK === */}
        <path
          d="M86 80 L86 92 Q86 98 92 102 L84 108 L84 114 L156 114 L156 108 L148 102 Q154 98 154 92 L154 80 Z"
          fill={`url(#${id}-glass)`}
          stroke="#3A4254"
          strokeWidth="0.5"
          strokeOpacity="0.4"
        />

        {/* === GLASS BODY === */}
        <path
          d="M70 114 L70 320 Q70 332 84 332 L156 332 Q170 332 170 320 L170 114 Z"
          fill={`url(#${id}-glass)`}
          stroke="#5EEAD4"
          strokeWidth="0.4"
          strokeOpacity="0.25"
        />

        {/* glass shine */}
        <rect x="76" y="118" width="3" height="200" fill="#FFFFFF" opacity="0.18" />
        <rect x="80" y="118" width="1" height="200" fill="#FFFFFF" opacity="0.10" />

        {/* === LYOPHILISED POWDER PILE (white) === */}
        <path
          d="M70 304 Q90 296 110 300 Q130 294 150 302 Q160 308 170 306 L170 320 Q170 332 156 332 L84 332 Q70 332 70 320 Z"
          fill={`url(#${id}-powder`}
        />
        {/* powder texture flecks */}
        {[
          [88, 308], [102, 304], [118, 306], [136, 302], [152, 308], [98, 314], [128, 312], [144, 316]
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="0.8" fill="#FFFFFF" opacity="0.6" />
        ))}

        {/* === LABEL === */}
        <rect x="68" y="138" width="104" height="160" fill={`url(#${id}-label)`} stroke="#1A2940" strokeWidth="0.4" />

        {/* Rising line graph logo (matches reference photo brand) */}
        <g transform="translate(94, 150)">
          {/* Ascending line with circles */}
          <path
            d="M0 18 L8 16 L18 14 L28 8 L38 4 L46 0"
            stroke={`url(#${id}-accent)`}
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
          />
          {/* Bar chart at end */}
          <rect x="44" y="2" width="1.5" height="14" fill="#A6FF00" opacity="0.85" />
          <rect x="47" y="0" width="1.5" height="16" fill="#A6FF00" opacity="0.85" />
          <rect x="50" y="-2" width="1.5" height="18" fill="#A6FF00" opacity="0.85" />
          {/* Arrow tip */}
          <path d="M48 -3 L52 -3 L50 -7 Z" fill="#A6FF00" />
          {/* Nodes */}
          <circle cx="0" cy="18" r="1.6" fill="#A6FF00" />
          <circle cx="8" cy="16" r="1.6" fill="#9BF089" />
          <circle cx="18" cy="14" r="1.6" fill="#7BEDB8" />
          <circle cx="28" cy="8" r="1.6" fill="#5EEAD4" />
          <circle cx="38" cy="4" r="1.6" fill="#3FD0DC" />
        </g>

        {/* PEPTIGEN wordmark */}
        <text x="120" y="186" textAnchor="middle" fill="#F7FAFC" fontSize="11" fontFamily="'Exo 2', sans-serif" fontWeight="800" letterSpacing="2.6">
          PEPTIGEN
        </text>
        <text x="120" y="195" textAnchor="middle" fill="#5EEAD4" fontSize="5.2" fontFamily="'Exo 2', sans-serif" letterSpacing="3.4">
          L A B S
        </text>

        {/* Product name (auto-sized) */}
        <text
          x="120"
          y="220"
          textAnchor="middle"
          fill="#F7FAFC"
          fontSize={nameFontSize}
          fontFamily="'Exo 2', sans-serif"
          fontWeight="800"
          letterSpacing="0.6"
        >
          {name}
        </text>
        {/* Strength */}
        <text x="120" y="234" textAnchor="middle" fill="#5EEAD4" fontSize="9" fontFamily="'Exo 2', sans-serif" fontWeight="600" letterSpacing="0.5">
          {product?.strength || ""}
        </text>
        {/* Accent divider */}
        <line x1="84" y1="240" x2="156" y2="240" stroke={`url(#${id}-accent)`} strokeWidth="0.8" />

        {/* LYOPHILIZED POWDER */}
        <text x="120" y="252" textAnchor="middle" fill="#F7FAFC" fontSize="4.6" fontFamily="'JetBrains Mono', monospace" letterSpacing="1.6">
          {product?.form === "Sterile solution" ? "STERILE SOLUTION" : "LYOPHILIZED POWDER"}
        </text>

        {/* Compliance text */}
        <text x="84" y="266" fill="#F7FAFC" fontSize="3.8" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          RESEARCH USE ONLY
        </text>
        <text x="84" y="272" fill="#F7FAFC" fontSize="3.8" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          NOT FOR HUMAN CONSUMPTION
        </text>
        <text x="84" y="278" fill="#F7FAFC" fontSize="3.8" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          STORE AT {(product?.storage || "2-8°C").toUpperCase()}
        </text>

        {/* LOT */}
        <text x="84" y="290" fill="#5EEAD4" fontSize="4.4" fontFamily="'JetBrains Mono', monospace" letterSpacing="1" fontWeight="700">
          LOT: {(product?.batch_number || "PL-" + (product?.sku || "00000").slice(-5)).toUpperCase()}
        </text>
      </svg>
    </div>
  );
}

/**
 * Stock-out placeholder — replaces the product image entirely on cards and
 * detail pages where stock_status === 'out_of_stock'. Keeps the brand
 * aesthetic (dark navy + cyan/lime accents) while making the unavailability
 * unmissable so customers don't waste effort considering it.
 */
function OutOfStockPlaceholder({ productName }) {
  return (
    <div
      className="relative w-full h-full overflow-hidden flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at 30% 30%, rgba(40,90,140,0.20) 0%, transparent 55%), linear-gradient(180deg, #06101F 0%, #020812 100%)",
      }}
      aria-label={`${productName || "Product"} — currently out of stock`}
      data-testid="out-of-stock-image"
    >
      {/* Diagonal hatch pattern (subtle) */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" aria-hidden="true">
        <defs>
          <pattern id="oos-hatch" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="14" stroke="#5EEAD4" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#oos-hatch)" />
      </svg>

      {/* Centred stamp */}
      <div className="relative z-10 flex flex-col items-center text-center px-4">
        {/* Empty-vial silhouette icon */}
        <svg width="56" height="78" viewBox="0 0 56 78" className="mb-4" aria-hidden="true">
          {/* cap */}
          <rect x="14" y="2" width="28" height="6" rx="1" fill="#5C6678" opacity="0.5" />
          {/* crimp */}
          <rect x="13" y="8" width="30" height="6" fill="#9098A8" opacity="0.45" />
          {/* glass body */}
          <path
            d="M10 18 L10 70 Q10 76 16 76 L40 76 Q46 76 46 70 L46 18 Z"
            fill="none"
            stroke="#5EEAD4"
            strokeWidth="1.4"
            strokeOpacity="0.5"
          />
          {/* diagonal slash through it */}
          <line
            x1="6"
            y1="6"
            x2="50"
            y2="74"
            stroke="#FF8A5B"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>

        <div
          className="pg-mono text-[10px] uppercase tracking-[0.32em] text-[#FF8A5B] mb-2 font-bold"
          data-testid="out-of-stock-stamp"
        >
          Out of Stock
        </div>
        <div
          className="text-[#F7FAFC] text-base sm:text-lg leading-tight"
          style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: "0.04em" }}
        >
          Currently unavailable
        </div>
        <div className="pg-mono text-[9px] uppercase tracking-[0.22em] text-[#9CA8B8] mt-2">
          Restock alerts available below
        </div>
      </div>
    </div>
  );
}
